import { ConfirmationModalFooter } from '@/components/ConfirmationModal/Base/Footer';
import { useRejectReport } from '@/features/approving_invoice_reports/components/ConfirmRejectModal/hooks/useRejectReport';
import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import { ButtonV2Props, Modal } from '@moneyforward/ap-frontend-components';
import { FC, memo, useMemo } from 'react';
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form';
import { Contents } from './Contents';
import { useConfirmRejectModal } from './context/ConfirmRejectModalContext';
import { ConfirmRejectForm } from './types';

type InnerConfirmRejectModalProps = {
  methods: UseFormReturn<ConfirmRejectForm>;
  onDrawerOpen: (id: string) => void;
  onDrawerClose: () => void;
};

export const InnerConfirmRejectModal: FC<InnerConfirmRejectModalProps> = memo(
  ({ methods, onDrawerOpen, onDrawerClose }) => {
    const { open, onCloseConfirmRejectModal, reportId } =
      useConfirmRejectModal();
    const containerRef = useGlobalContainerRef();
    const { rejectReport } = useRejectReport({ onDrawerOpen, onDrawerClose });

    const { t } = useTranslation();
    const cancelButtonProps: ButtonV2Props = useMemo(
      () => ({
        isSecondary: true,
        isTertiary: true,
        onClick: onCloseConfirmRejectModal,
        size: 'sm',
        children: t('cancel'),
      }),
      [onCloseConfirmRejectModal, t]
    );
    const okButtonProps: ButtonV2Props = useMemo(
      () => ({
        color: 'danger',
        onClick: rejectReport(reportId),
        size: 'sm',
        isSecondary: true,
        children: t('approving_invoice_reports.reject_modal.button.reject'),
        isDisabled: !methods.formState.isValid,
      }),
      [rejectReport, reportId, methods.formState.isValid, t]
    );

    return (
      <Modal
        open={open}
        onClose={onCloseConfirmRejectModal}
        size='md'
        title={t('approving_invoice_reports.reject_modal.title')}
        getContainer={containerRef}
        mask
        footer={
          <ConfirmationModalFooter
            cancelButton={cancelButtonProps}
            okButton={okButtonProps}
          />
        }
      >
        <Contents reportId={reportId} onCloseDetailDrawer={onDrawerClose} />
      </Modal>
    );
  }
);

export const ConfirmRejectModal: FC<{
  onDrawerOpen: (id: string) => void;
  onDrawerClose: () => void;
}> = memo(({ onDrawerOpen, onDrawerClose }) => {
  const methods = useForm<ConfirmRejectForm>({
    mode: 'all',
    defaultValues: {
      rejectDestination: '',
      comment: '',
    },
    reValidateMode: 'onChange',
  });
  return (
    <FormProvider {...methods}>
      <InnerConfirmRejectModal
        methods={methods}
        onDrawerOpen={onDrawerOpen}
        onDrawerClose={onDrawerClose}
      />
    </FormProvider>
  );
});

InnerConfirmRejectModal.displayName = 'InnerConfirmRejectModal';
ConfirmRejectModal.displayName = 'ConfirmRejectModal';
